import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { API } from "@/lib/engagements";

const ROLES = ["staff", "supervisor", "admin"];
const ROLE_STYLE = {
  admin: "bg-zinc-900 text-white border-zinc-900",
  supervisor: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
  staff: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function Staff() {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", staff_id: "", email: "", password: "", role: "staff", start_date: "", end_date: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", staff_id: "", email: "", role: "staff", password: "", start_date: "", end_date: "" });

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/auth/staff`);
    setStaff(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/staff`, form);
      toast.success(`${form.name} can now sign in`);
      setForm({ name: "", staff_id: "", email: "", password: "", role: "staff", start_date: "", end_date: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({
      name: s.name || "",
      staff_id: s.staff_id || "",
      email: s.email || "",
      role: s.role || "staff",
      password: "",
      start_date: s.start_date || "",
      end_date: s.end_date || "",
    });
  };

  const saveEdit = async () => {
    try {
      if (!isAdmin) {
        await axios.put(`${API}/auth/staff/${editing.id}/dates`, {
          start_date: editForm.start_date || null,
          end_date: editForm.end_date || null,
        });
      } else {
        const payload = { ...editForm };
        if (!payload.password) delete payload.password;
        await axios.put(`${API}/auth/staff/${editing.id}`, payload);
      }
      toast.success("Profile updated");
      setEditing(null);
      load();
      if (editing.id === user?.id) refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/auth/staff/${id}`);
      toast.success("Staff removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-3xl font-extrabold">Staff accounts</h2>
        <p className="mt-1 text-sm text-zinc-500">
          {isAdmin
            ? "Staff log their own hours. Supervisors can view every timesheet and set staff date ranges but cannot key in client work. Admins have full access."
            : "You can set each staff member's start and end dates. Only admins can add, rename or remove accounts."}
        </p>
      </div>

      <form
        data-testid="staff-form"
        onSubmit={add}
        className={`grid grid-cols-1 gap-4 border border-border bg-white p-5 md:grid-cols-4 ${
          isAdmin ? "" : "hidden"
        }`}
      >
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label>
          <Input data-testid="staff-name" required value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Staff ID</Label>
          <Input data-testid="staff-id" required value={form.staff_id}
                 onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
                 placeholder="e.g. rna001" className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
          <Input data-testid="staff-email" type="email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 placeholder="optional" className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
          <Input data-testid="staff-password" type="password" required minLength={6} value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Role</Label>
          <select data-testid="staff-role" value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm">
            {ROLES.map((r) => (
              <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Start date</Label>
          <Input data-testid="staff-start-date" type="date" value={form.start_date}
                 onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                 className="mt-1.5 rounded-sm tabular" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">End date</Label>
          <Input data-testid="staff-end-date" type="date" value={form.end_date}
                 onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                 className="mt-1.5 rounded-sm tabular" />
        </div>
        <div className="flex items-end">
          <Button data-testid="staff-submit" type="submit" disabled={busy}
                  className="w-full rounded-sm bg-black text-white hover:bg-zinc-800 hover:text-white">
            {busy ? "Adding…" : "Add user"}
          </Button>
        </div>
      </form>

      <div className="overflow-x-auto border border-border bg-white">
        <div className="grid min-w-[960px] grid-cols-[1fr_120px_1.2fr_110px_120px_120px_90px] bg-zinc-900 px-4 py-2 text-[10px] uppercase tracking-widest text-white">
          <span>Name</span>
          <span>Staff ID</span>
          <span>Email</span>
          <span>Role</span>
          <span>Start date</span>
          <span>End date</span>
          <span className="text-right">Actions</span>
        </div>
        {staff.map((s) => (
          <div key={s.id} data-testid={`staff-row-${s.staff_id}`}
               className="grid min-w-[960px] grid-cols-[1fr_120px_1.2fr_110px_120px_120px_90px] items-center border-t border-border px-4 py-3 text-sm">
            <span className="font-semibold">{s.name}</span>
            <span className="font-mono text-zinc-600">{s.staff_id}</span>
            <span className="truncate text-zinc-600">{s.email || "—"}</span>
            <span>
              <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[11px] font-medium capitalize ${ROLE_STYLE[s.role]}`}>
                {s.role}
              </span>
            </span>
            <span data-testid={`staff-start-${s.staff_id}`} className="tabular text-zinc-600">
              {s.start_date || "—"}
            </span>
            <span data-testid={`staff-end-${s.staff_id}`} className="tabular text-zinc-600">
              {s.end_date || "—"}
            </span>
            <span className="flex items-center justify-end gap-3">
              <button data-testid={`staff-edit-${s.staff_id}`} onClick={() => openEdit(s)}
                      className="text-zinc-400 transition-colors hover:text-zinc-900">
                <Pencil className="h-4 w-4" />
              </button>
              {isAdmin && s.id !== user?.id && (
                <button data-testid={`staff-delete-${s.staff_id}`} onClick={() => remove(s.id)}
                        className="text-zinc-400 transition-colors hover:text-red-700">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}            </span>
          </div>
        ))}
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="staff-edit-dialog" className="bg-white rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">Edit profile</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {isAdmin
                ? "Update name, staff ID, email, role or dates. Leave password blank to keep the current one."
                : "Set this person's start and end dates. Only admins can change name, staff ID, email or role."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Start date</Label>
                <Input data-testid="edit-start-date" type="date" value={editForm.start_date}
                       onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                       className="mt-1.5 rounded-sm tabular" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">End date</Label>
                <Input data-testid="edit-end-date" type="date" value={editForm.end_date}
                       onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                       className="mt-1.5 rounded-sm tabular" />
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">
              Staff can only log timesheet entries inside this range. Leave blank for no limit.
            </p>
            {isAdmin && (
            <>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label>
              <Input data-testid="edit-name" value={editForm.name}
                     onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                     className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Staff ID</Label>
              <Input data-testid="edit-staff-id" value={editForm.staff_id}
                     onChange={(e) => setEditForm({ ...editForm, staff_id: e.target.value })}
                     className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
              <Input data-testid="edit-email" type="email" value={editForm.email}
                     onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                     className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Role</Label>
              <select data-testid="edit-role" value={editForm.role}
                      disabled={editing?.id === user?.id}
                      onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                      className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm disabled:bg-zinc-100">
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>
                ))}
              </select>
              {editing?.id === user?.id && (
                <p className="mt-1 text-[11px] text-zinc-500">You cannot change your own role.</p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">New password</Label>
              <Input data-testid="edit-password" type="password" value={editForm.password}
                     onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                     placeholder="leave blank to keep" className="mt-1.5 rounded-sm" />
            </div>
            </>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button data-testid="edit-save" onClick={saveEdit}
                    className="rounded-sm bg-black text-white hover:bg-zinc-800 hover:text-white">
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
